var postEventsToExcess = require("./postEventsToExcess");

var EXCESS_EVENT_URL = "https://excess.duolingo.com/batch";
var EXCESS_EVENT_DEBUG_URL = "https://excess-dev.duolingo.com/batch";

var debugMode = false;
var distinctId = undefined;
var superProperties = {};

module.exports = {
  /**
   * This function initializes the Excess tracking object.
   */
  init: function () {
    distinctId = util.generateUUID();
  },

  /**
   * Enable or Disable debug mode.
   *
   * @param {Boolean} debug A boolean to set the mode with.
   */
  setDebug: function (debug) {
    debugMode = debug;
  },

  /**
   * Identify a user with a unique ID. All subsequent
   * actions caused by this user will be tied to this unique ID. This
   * property is used to track unique visitors. If the method is
   * never called, then unique visitors will be identified by a UUID
   * generated the first time they visit the site.
   *
   * @param {String} [uniqueId] A string that uniquely identifies a user.
   *  If not provided, a randomly generated ID will be used.
   */
  identify: function (uniqueId) {
    distinctId = uniqueId;
  },

  /**
   * Create an alias, which will be used to link two distinct_ids.
   * Multiple aliases can map to the same original ID, but not vice-versa.
   *
   * Typically called after a new user is created, but before calling
   * `identify(...)`, in order to link the newly assigned user id with
   * the current (and likely randomly generated) distinct id.
   *
   * @param {String} aliasId An id that you want to use for this user.
   *  This is usually a logged in user id.
   * @param {String} [originalId] The current id for this user.
   *  If not provided, the current distinct_id will be used.
   * @param {Function} [callback] An optional callback function. This
   * will always be called when all tracking is finished, even if the
   * tracking failed.
   */
  alias: function (aliasId, originalId, callback) {
    if (originalId === undefined) {
      originalId = this.getDistinctId();
    }

    if (aliasId !== originalId) {
      this.track(
        "$create_alias",
        {
          alias: aliasId,
          distinct_id: originalId,
        },
        callback
      );
    } else {
      console.warn("alias id matches original id");
      // Call the callback anyway.
      callback && callback();
    }
  },

  /**
   * Retrieves the distinct ID as set by `identify`. If not yet set,
   * this returns the default value, which is a random UUID.
   */
  getDistinctId: function () {
    return distinctId;
  },

  /**
   * Register a set of super properties, which are included with all
   * events. This will overwrite previous super property values.
   *
   * ### Usage:
   *
   *   // register 'Gender' as a super property
   *   register({'Gender': 'Female'});
   *
   *   // register several super properties when a user signs up
   *   register({
   *     'Email': 'jdoe@example.com',
   *     'Account Type': 'Free'
   *   });
   *
   * @param {Object} properties Properties to store about the user
   */
  register: function (properties) {
    superProperties = Object.assign({}, superProperties, properties);
  },

  /**
   * Track an event.
   *
   * ### Usage:
   *
   *   // track an event named 'Registered'
   *   track('Registered', {'Gender': 'Male', 'Age': 21});
   *
   * @param {String} eventName The name of the event.
   * @param {Object} [properties] Optional event specific properties.
   * @param {Function} [callback] An optional callback function. This
   * will always be called when all tracking is finished, even if the
   * tracking failed.
   */
  track: function (eventName, properties, callback) {
    // Set some super properties if they haven't been set yet
    var unsetPropUpdates = Object.assign(
      {},
      util.info.campaignParams(),
      util.info.referrerParams(window.document.referrer)
    );
    Object.keys(unsetPropUpdates).forEach(function (k) {
      if (!superProperties.hasOwnProperty(k)) {
        superProperties[k] = unsetPropUpdates[k];
      }
    });

    // mixpanel-style event properties
    var propertyData = Object.assign(
      {},
      util.info.searchInfo(window.document.referrer),
      util.info.properties(),
      superProperties,
      { distinct_id: this.getDistinctId() },
      properties
    );

    // Ensure that the alias and distinct_id are strings
    if (propertyData.alias) {
      propertyData.alias = propertyData.alias + "";
    }
    propertyData.distinct_id = propertyData.distinct_id + "";

    // excess-style event
    var excessData = {
      event_type: eventName,
      event_timestamp: new Date().getTime(),
      attributes: propertyData,
      client: { client_id: "web-excess" },
    };

    if (util.isBlockedUA(navigator.userAgent)) {
      callback && callback();
      return;
    }

    postEventsToExcess(
      debugMode ? EXCESS_EVENT_DEBUG_URL : EXCESS_EVENT_URL,
      [excessData],
      callback
    );
  },
};

/**
 * Utility methods adapted from mixpanel-browser library.
 */
var util = {
  /**
   * Generates a random string UUID.
   */
  generateUUID: function () {
    // Time/ticks information
    // 1*new Date() is a cross browser version of Date.now()
    var T = function () {
      var d = 1 * new Date(),
        i = 0;

      // this while loop figures how many browser ticks go by
      // before 1*new Date() returns a new number, ie the amount
      // of ticks that go by per millisecond
      while (d === 1 * new Date()) {
        i++;
      }

      return d.toString(16) + i.toString(16);
    };

    // Math.Random entropy
    var R = function () {
      return Math.random().toString(16).replace(".", "");
    };

    // User agent entropy
    // This function takes the user agent string, and then xors
    // together each sequence of 8 bytes.  This produces a final
    // sequence of 8 bytes which it returns as hex.
    var UA = function () {
      var ua = navigator.userAgent,
        i,
        ch,
        buffer = [],
        ret = 0;

      function xor(result, byte_array) {
        var j,
          tmp = 0;
        for (j = 0; j < byte_array.length; j++) {
          tmp |= buffer[j] << (j * 8);
        }
        return result ^ tmp;
      }

      for (i = 0; i < ua.length; i++) {
        ch = ua.charCodeAt(i);
        buffer.unshift(ch & 0xff);
        if (buffer.length >= 4) {
          ret = xor(ret, buffer);
          buffer = [];
        }
      }

      if (buffer.length > 0) {
        ret = xor(ret, buffer);
      }

      return ret.toString(16);
    };

    var se = (screen.height * screen.width).toString(16);
    return T() + "-" + R() + "-" + UA() + "-" + se + "-" + T();
  },

  // This is to block various web spiders from executing our JS and
  // sending false tracking data
  isBlockedUA: function (ua) {
    var bad = [
      "google web preview",
      "baiduspider",
      "yandexbot",
      "bingbot",
      "googlebot",
      "yahoo! slurp",
    ];
    var regex = new RegExp("(" + bad.join("|") + ")", "i");
    return regex.test(ua);
  },

  /**
   * Informational utility methods, used to generate base properties.
   */
  info: {
    stripEmptyProperties: function (p) {
      var ret = {};
      Object.keys(p).forEach(function (k) {
        if (
          Object.prototype.toString.call(p[k]) === "[object String]" &&
          p[k].length > 0
        ) {
          ret[k] = p[k];
        }
      });
      return ret;
    },

    getQueryParam: function (url, param) {
      // Expects a raw URL
      param = param.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
      var regexS = "[\\?&]" + param + "=([^&#]*)",
        regex = new RegExp(regexS),
        results = regex.exec(url);
      if (
        results === null ||
        (results && typeof results[1] !== "string" && results[1].length)
      ) {
        return "";
      } else {
        return decodeURIComponent(results[1]).replace(/\+/g, " ");
      }
    },

    campaignParams: function () {
      var campaign_keywords =
          "utm_source utm_medium utm_campaign utm_content utm_term".split(" "),
        kw = "",
        params = {};
      campaign_keywords.forEach(function (kwkey) {
        kw = util.info.getQueryParam(document.URL, kwkey);
        if (kw.length) {
          params[kwkey] = kw;
        }
      });

      return params;
    },

    searchEngine: function (referrer) {
      if (referrer.search("https?://(.*)google.([^/?]*)") === 0) {
        return "google";
      } else if (referrer.search("https?://(.*)bing.com") === 0) {
        return "bing";
      } else if (referrer.search("https?://(.*)yahoo.com") === 0) {
        return "yahoo";
      } else if (referrer.search("https?://(.*)duckduckgo.com") === 0) {
        return "duckduckgo";
      } else {
        return null;
      }
    },

    searchInfo: function (referrer) {
      var search = util.info.searchEngine(referrer),
        param = search !== "yahoo" ? "q" : "p",
        ret = {};

      if (search !== null) {
        ret["$search_engine"] = search;

        var keyword = util.info.getQueryParam(referrer, param);
        if (keyword.length) {
          ret["mp_keyword"] = keyword;
        }
      }

      return ret;
    },

    /**
     * This function detects which browser is running this script.
     * The order of the checks are important since many user agents
     * include key words used in later checks.
     */
    browser: function (user_agent, vendor, opera) {
      var includes = function (str, needle) {
        return str.indexOf(needle) !== -1;
      };
      vendor = vendor || ""; // vendor is undefined for at least IE9
      if (opera || includes(user_agent, " OPR/")) {
        if (includes(user_agent, "Mini")) {
          return "Opera Mini";
        }
        return "Opera";
      } else if (/(BlackBerry|PlayBook|BB10)/i.test(user_agent)) {
        return "BlackBerry";
      } else if (
        includes(user_agent, "IEMobile") ||
        includes(user_agent, "WPDesktop")
      ) {
        return "Internet Explorer Mobile";
      } else if (includes(user_agent, "Edge")) {
        return "Microsoft Edge";
      } else if (includes(user_agent, "FBIOS")) {
        return "Facebook Mobile";
      } else if (includes(user_agent, "Chrome")) {
        return "Chrome";
      } else if (includes(user_agent, "CriOS")) {
        return "Chrome iOS";
      } else if (
        includes(user_agent, "UCWEB") ||
        includes(user_agent, "UCBrowser")
      ) {
        return "UC Browser";
      } else if (includes(user_agent, "FxiOS")) {
        return "Firefox iOS";
      } else if (includes(vendor, "Apple")) {
        if (includes(user_agent, "Mobile")) {
          return "Mobile Safari";
        }
        return "Safari";
      } else if (includes(user_agent, "Android")) {
        return "Android Mobile";
      } else if (includes(user_agent, "Konqueror")) {
        return "Konqueror";
      } else if (includes(user_agent, "Firefox")) {
        return "Firefox";
      } else if (
        includes(user_agent, "MSIE") ||
        includes(user_agent, "Trident/")
      ) {
        return "Internet Explorer";
      } else if (includes(user_agent, "Gecko")) {
        return "Mozilla";
      } else {
        return "";
      }
    },

    /**
     * This function detects which browser version is running this script,
     * parsing major and minor version (e.g., 42.1). User agent strings from:
     * http://www.useragentstring.com/pages/useragentstring.php
     */
    browserVersion: function (userAgent, vendor, opera) {
      var browser = util.info.browser(userAgent, vendor, opera);
      var versionRegexs = {
        "Internet Explorer Mobile": /rv:(\d+(\.\d+)?)/,
        "Microsoft Edge": /Edge\/(\d+(\.\d+)?)/,
        Chrome: /Chrome\/(\d+(\.\d+)?)/,
        "Chrome iOS": /CriOS\/(\d+(\.\d+)?)/,
        "UC Browser": /(UCBrowser|UCWEB)\/(\d+(\.\d+)?)/,
        Safari: /Version\/(\d+(\.\d+)?)/,
        "Mobile Safari": /Version\/(\d+(\.\d+)?)/,
        Opera: /(Opera|OPR)\/(\d+(\.\d+)?)/,
        Firefox: /Firefox\/(\d+(\.\d+)?)/,
        "Firefox iOS": /FxiOS\/(\d+(\.\d+)?)/,
        Konqueror: /Konqueror:(\d+(\.\d+)?)/,
        BlackBerry: /BlackBerry (\d+(\.\d+)?)/,
        "Android Mobile": /android\s(\d+(\.\d+)?)/,
        "Internet Explorer": /(rv:|MSIE )(\d+(\.\d+)?)/,
        Mozilla: /rv:(\d+(\.\d+)?)/,
      };
      var regex = versionRegexs[browser];
      if (regex === undefined) {
        return null;
      }
      var matches = userAgent.match(regex);
      if (!matches) {
        return null;
      }
      return parseFloat(matches[matches.length - 2]);
    },

    os: function () {
      var a = navigator.userAgent;
      if (/Windows/i.test(a)) {
        if (/Phone/.test(a) || /WPDesktop/.test(a)) {
          return "Windows Phone";
        }
        return "Windows";
      } else if (/(iPhone|iPad|iPod)/.test(a)) {
        return "iOS";
      } else if (/Android/.test(a)) {
        return "Android";
      } else if (/(BlackBerry|PlayBook|BB10)/i.test(a)) {
        return "BlackBerry";
      } else if (/Mac/i.test(a)) {
        return "Mac OS X";
      } else if (/Linux/.test(a)) {
        return "Linux";
      } else if (/CrOS/.test(a)) {
        return "Chrome OS";
      } else {
        return "";
      }
    },

    device: function (user_agent) {
      if (/Windows Phone/i.test(user_agent) || /WPDesktop/.test(user_agent)) {
        return "Windows Phone";
      } else if (/iPad/.test(user_agent)) {
        return "iPad";
      } else if (/iPod/.test(user_agent)) {
        return "iPod Touch";
      } else if (/iPhone/.test(user_agent)) {
        return "iPhone";
      } else if (/(BlackBerry|PlayBook|BB10)/i.test(user_agent)) {
        return "BlackBerry";
      } else if (/Android/.test(user_agent)) {
        return "Android";
      } else {
        return "";
      }
    },

    referringDomain: function (referrer) {
      var split = referrer.split("/");
      if (split.length >= 3) {
        return split[2];
      }
      return "";
    },

    referrerParams: function (referrer) {
      return {
        $initial_referrer: referrer || "$direct",
        $initial_referring_domain:
          util.info.referringDomain(referrer) || "$direct",
      };
    },

    properties: function () {
      var userAgent = navigator.userAgent;
      var windowOpera = window.opera;
      return Object.assign(
        {},
        util.info.stripEmptyProperties({
          $os: util.info.os(),
          $browser: util.info.browser(userAgent, navigator.vendor, windowOpera),
          $referrer: document.referrer,
          $referring_domain: util.info.referringDomain(document.referrer),
          $device: util.info.device(userAgent),
        }),
        {
          $current_url: window.location.href,
          $browser_version: util.info.browserVersion(
            userAgent,
            navigator.vendor,
            windowOpera
          ),
          $screen_height: screen.height,
          $screen_width: screen.width,
          mp_lib: "web",
          $lib_version: "2.21.0d",
        }
      );
    },
  },
};
